// Utilitários do modelo "Metrópole" (topo 15 · busca 18 · card 12 · rodapé 12) — linguagem visual de um grande
// portal imobiliário de referência (site BRANCO, uma única cor de ACENTO vermelha usada em botões, abas, links
// "Ver mais", corações e marcadores; texto grafite #2a2a2a; cards brancos de borda fina e raio pequeno; Open Sans)
// reproduzida em CSS modules NOSSOS, com as CORES DO TENANT: o acento vira a cor saturada do tenant. Só as 4 peças
// do Metrópole importam daqui — não é componente compartilhado dos ~300 sites. AUTOSSUFICIENTE: depende só dos
// helpers genéricos do Horizonte/Atelier/Larissa (contatos, menu, redes, WhatsApp, URL/foto/preço do imóvel).
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ensureReadableTextColor, getContrastRatio, isColorLight } from '../../../utils/FunctionsGlobals';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, urlDoImovel, urlDaListagem, tituloDoImovel, finalidadeDoImovel, escadaDeValores, valorCompacto } from '../atelier/atelier';
export type { OpcaoValor } from '../atelier/atelier';
export { atributosDoImovel, precosRotulados, lugarDoImovel, semCaixaAlta } from '../larissa/larissa';
export type { PrecoRotulado } from '../larissa/larissa';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8 a 11.
export const EASE_METROPOLE = [0.22, 1, 0.36, 1] as const;

/** Escala de RESERVA (tokens medidos no site de referência): entra só onde o tenant não tem cor utilizável. */
export const RESERVA = {
    /** vermelho do portal (rgb 235,25,75) — vira a cor saturada do tenant quando ela existe */
    acento: '#eb194b',
    /** texto principal (títulos, nomes) */
    tinta: '#2a2a2a',
    /** texto corrente */
    texto: '#484848',
    /** texto secundário (lê 5,0:1 sobre branco) */
    cinza: '#6f6f6f',
    /** bordas dos cards e divisórias */
    borda: '#dfdfdf',
    /** divisórias mais leves */
    bordaSuave: '#ececec',
    /** fundo das faixas claras (bloco de SEO, serviços) */
    fundo: '#f7f7f8',
};

export interface PaletaMetropole {
    /** cor de ACENTO: botões primários, aba ativa, links "Ver mais", corações, marcadores, faixa do rodapé (texto branco lê ≥ 4,5:1). */
    acento: string;
    /** acento mais fundo (hover dos botões). */
    acentoHover: string;
    /** tinta leve do acento sobre branco (8%): fundo de hover, pílulas, "NOVO". */
    acentoSuave: string;
    /** painel claro tingido pelo acento (10%): bloco de financiamento/serviços. */
    painel: string;
    /** texto sobre o acento (sempre legível). */
    sobreAcento: string;
    /** texto principal sobre branco. */
    tinta: string;
    /** texto corrente sobre branco. */
    texto: string;
    /** texto secundário sobre branco/fundo claro (≥ 4,5:1). */
    cinza: string;
    /** bordas dos cards e campos. */
    borda: string;
    /** divisórias leves. */
    bordaSuave: string;
    /** fundo das faixas claras. */
    fundo: string;
}

const hexValido = (v: unknown): string => {
    let h = String(v ?? '')
        .trim()
        .toLowerCase();
    if (!h) return '';
    if (!h.startsWith('#')) h = `#${h}`;
    if (/^#[0-9a-f]{3}$/.test(h)) h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    return /^#[0-9a-f]{6}$/.test(h) ? h : '';
};

/** Saturação HSL (0–1). */
const saturacao = (hex: string): number => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max === min) return 0;
    const l = (max + min) / 2;
    const d = max - min;
    return l > 0.5 ? d / (2 - max - min) : d / (max + min);
};
/** luminância relativa (0–1) via contraste com o preto. */
const luminancia = (hex: string): number => getContrastRatio(hex, '#000000') * 0.05 - 0.05;
/** serve de acento: saturada (não cinza) e nem clara demais (amarelo-limão/branco não seguram texto branco). */
const serveDeAcento = (hex: string): boolean => Boolean(hex) && saturacao(hex) >= 0.18 && luminancia(hex) < 0.6;

/** Puxa `cor` para `alvo` até `texto` ler ≥ `minimo` sobre ela (passos de 5%). */
const ajustarAte = (cor: string, alvo: string, texto: string, minimo: number): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? cor : misturarCores(cor, alvo, t);
        if (getContrastRatio(texto, c) >= minimo) return c;
    }
    return alvo;
};

/**
 * Mapeamento p/ os 300 sites: acento = a 1ª cor SATURADA do tenant (cor1; senão cor2), escurecida só o necessário
 * para texto branco ler ≥ 4,5:1 (o vermelho da referência lê 4,4:1 e também é ajustado). Sem cor saturada no tenant
 * (site só grafite/cinza) → vermelho de reserva, porque o modelo É "branco + um acento": sem acento ele vira um
 * site cinza. Texto, cinzas, bordas e fundos são a escala NEUTRA da referência (o portal não tinge os cinzas).
 */
export const paletaMetropole = (site: any): PaletaMetropole => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bruta = [cor1, cor2].find(serveDeAcento) ?? '';
    const acento = ajustarAte(bruta || RESERVA.acento, '#000000', '#ffffff', 4.5);
    return {
        acento,
        acentoHover: misturarCores(acento, '#000000', 0.14),
        acentoSuave: misturarCores(acento, '#ffffff', 0.92),
        painel: misturarCores(acento, '#ffffff', 0.9),
        sobreAcento: '#ffffff',
        tinta: RESERVA.tinta,
        texto: RESERVA.texto,
        cinza: RESERVA.cinza,
        borda: RESERVA.borda,
        bordaSuave: RESERVA.bordaSuave,
        fundo: RESERVA.fundo,
    };
};

/** Variáveis CSS (--mt-*) que os 4 módulos consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsMetropole = (site: any): CSSProperties => {
    const p = paletaMetropole(site);
    return {
        '--mt-acento': p.acento,
        '--mt-acento-hover': p.acentoHover,
        '--mt-acento-suave': p.acentoSuave,
        '--mt-painel': p.painel,
        '--mt-sobre-acento': p.sobreAcento,
        '--mt-tinta': p.tinta,
        '--mt-texto': p.texto,
        '--mt-cinza': p.cinza,
        '--mt-borda': p.borda,
        '--mt-borda-suave': p.bordaSuave,
        '--mt-fundo': p.fundo,
        '--mt-sombra': 'rgba(42, 42, 42, 0.08)',
        '--mt-sombra-forte': 'rgba(42, 42, 42, 0.16)',
        '--mt-acento-veu': hexParaRgba(p.acento, 0.85, 'rgba(235, 25, 75, ALPHA)'),
        '--mt-fonte': "'Open Sans', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    } as CSSProperties;
};

/** Gate do modelo #12 "Metrópole" = topo 15 (mesmo critério de ehModeloHorizonte/ehModeloLarissa). */
export const ehModeloMetropole = (site: any): boolean => Number(site?.modelo_topo) === 15;

/**
 * Páginas internas no visual do modelo (/imoveis, ficha, /condominios, /empreendimento) — o mesmo esquema de
 * varsLarissaInterna: as vars do modelo + o que a PÁGINA precisa (texto legível sobre a cor5 do tenant, tons de
 * página, gutter comum e Open Sans na página toda). Fora do topo 15: null ⇒ nada muda.
 */
export const varsMetropoleInterna = (site: any): CSSProperties | null => {
    if (!ehModeloMetropole(site)) return null;
    const p = paletaMetropole(site);
    const fundo = hexValido(site?.cor5) || '#ffffff';
    const claro = isColorLight(fundo);
    const texto = claro ? ensureReadableTextColor(p.tinta, fundo) : ensureReadableTextColor(site?.cor13, fundo);
    const reserva = claro ? 'rgba(42, 42, 42, ALPHA)' : 'rgba(255, 255, 255, ALPHA)';
    return {
        ...varsMetropole(site),
        color: texto,
        '--fonte-principal': "'Open Sans', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        '--ap-gutter': 'clamp(16px, 4vw, 56px)',
        '--mt-fundo-pagina': fundo,
        '--mt-texto-pagina': texto,
        '--mt-texto-suave': claro ? ensureReadableTextColor(p.cinza, fundo) : hexParaRgba(texto, 0.72, reserva),
        '--mt-sutil': hexParaRgba(texto, 0.12, reserva),
        '--mt-sutil-forte': hexParaRgba(texto, 0.24, reserva),
        '--mt-painel-pagina': claro ? '#ffffff' : hexParaRgba(texto, 0.06, reserva),
    } as CSSProperties;
};

const lumDoPixel = (r: number, g: number, b: number): number => {
    const f = (v: number) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/**
 * O topo e o rodapé do Metrópole são BRANCOS: logo CLARA (branca/dourada, feita para fundo escuro — cs1722 é 100%
 * branca) some. Depois de montar, lê os pixels opacos da logo (a CDN manda CORS `*`): se menos de 60% deles leem
 * ≥ 2:1 sobre o branco, a logo ganha uma PLACA na cor de acento (raio 8). Logo escura, sem transparência (retângulo
 * já tem o próprio fundo) ou ilegível para o canvas (CORS/erro) → sem placa. Inverso do useLogoSemPlaca do Larissa.
 */
export const useLogoPrecisaPlaca = (src: string): boolean => {
    const [placa, setPlaca] = useState(false);
    useEffect(() => {
        setPlaca(false);
        if (!src || typeof window === 'undefined') return;
        let vivo = true;
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => {
            try {
                const lado = 48;
                const tela = document.createElement('canvas');
                tela.width = lado;
                tela.height = lado;
                const ctx = tela.getContext('2d', { willReadFrequently: true });
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, lado, lado);
                const d = ctx.getImageData(0, 0, lado, lado).data;
                let opacos = 0;
                let leSobreBranco = 0;
                for (let i = 0; i < d.length; i += 4) {
                    if (d[i + 3] < 128) continue;
                    opacos++;
                    if (1.05 / (lumDoPixel(d[i], d[i + 1], d[i + 2]) + 0.05) >= 2) leSobreBranco++;
                }
                if (!opacos || opacos / (lado * lado) > 0.9) return;
                if (vivo && leSobreBranco / opacos < 0.6) setPlaca(true);
            } catch {
                /* canvas sem leitura (CORS): fica sem placa */
            }
        };
        img.src = src;
        return () => {
            vivo = false;
        };
    }, [src]);
    return placa;
};

/** Ano corrente estável entre servidor e cliente (o rodapé imprime "© 2026"). */
export const anoAtual = (): number => new Date().getFullYear();
